import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Booking } from '../types';
import { bookingsAPI } from '../services/api';
import { Calendar, Clock, User, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp, MapPin, Navigation, Phone, Mail, Banknote, Star } from 'lucide-react';
import RatingModal from '../components/RatingModal';
import CancellationReasonModal from '../components/CancellationReasonModal';

const MyBookingsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPastBookings, setShowPastBookings] = useState(false);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [selectedBookingForRating, setSelectedBookingForRating] = useState<Booking | null>(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  // Auto-expand past bookings if there are recent completions (within last 7 days)
  useEffect(() => {
    if (bookings.length > 0) {
      const recentCompletions = bookings.filter(booking => {
        if (booking.status !== 'completed') return false;
        const completedDate = new Date(booking.updatedAt);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return completedDate >= sevenDaysAgo;
      });
      
      if (recentCompletions.length > 0) {
        setShowPastBookings(true);
      }
    }
  }, [bookings]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      // Fetch with a high limit to get all bookings (no pagination on frontend for now)
      // Backend will return all bookings if user is admin, otherwise returns customer bookings
      const response = await bookingsAPI.getMyBookings({ limit: 1000 });
      setBookings(response.data || []);
    } catch (error: any) {
      console.error('Error fetching bookings:', error);
      setError(error.response?.data?.message || 'Failed to load bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-blue-500" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDateTime = (dateTimeString: string | Date) => {
    const date = typeof dateTimeString === 'string' ? new Date(dateTimeString) : dateTimeString;
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStartDateTime = (booking: Booking): string => {
    // Use tripStartDateTime if available
    if (booking.tripStartDateTime) {
      return formatDateTime(booking.tripStartDateTime);
    }
    
    // Fallback to bookingDate if tripStartDateTime is not available
    if (!booking.bookingDate) {
      return 'N/A';
    }
    
    try {
      const bookingDate = typeof booking.bookingDate === 'string' 
        ? new Date(booking.bookingDate)
        : new Date(booking.bookingDate);
      
      if (isNaN(bookingDate.getTime())) {
        return 'N/A';
      }
      
      return formatDateTime(bookingDate);
    } catch (error) {
      console.error('Error calculating startDateTime:', error);
      return 'N/A';
    }
  };

  const getEndDateTime = (booking: Booking): string => {
    // Use tripEndDateTime if available
    if (booking.tripEndDateTime) {
      return formatDateTime(booking.tripEndDateTime);
    }
    
    // Fallback to bookingDate if tripEndDateTime is not available
    if (!booking.bookingDate) {
      return 'N/A';
    }
    
    try {
      const bookingDate = typeof booking.bookingDate === 'string' 
        ? new Date(booking.bookingDate)
        : new Date(booking.bookingDate);
      
      if (isNaN(bookingDate.getTime())) {
        return 'N/A';
      }
      
      return formatDateTime(bookingDate);
    } catch (error) {
      console.error('Error calculating endDateTime:', error);
      return 'N/A';
    }
  };

  // Separate bookings into upcoming and past with complex sorting
  const upcomingBookings = (() => {
    const activeBookings = bookings.filter(
      booking => booking.status === 'pending' || booking.status === 'confirmed'
    );
    
    if (activeBookings.length === 0) return [];
    
    // Sort: First booking is the most recently created (by createdAt), then descending by timeslot start time
    const sortedUpcomingBookings = activeBookings.sort((a, b) => {
      // First, check if one is more recent than the other
      const createdA = new Date(a.createdAt).getTime();
      const createdB = new Date(b.createdAt).getTime();
      
      // If created within last 24 hours, prioritize by creation time (most recent first)
      const now = Date.now();
      const dayInMs = 24 * 60 * 60 * 1000;
      const aIsRecent = (now - createdA) < dayInMs;
      const bIsRecent = (now - createdB) < dayInMs;
      
      // If both are recent or both are old, sort by creation time descending
      if (aIsRecent === bIsRecent) {
        if (createdA !== createdB) {
          return createdB - createdA; // Most recent first
        }
      } else if (aIsRecent) {
        return -1; // A is recent, B is not - A comes first
      } else {
        return 1; // B is recent, A is not - B comes first
      }
      
      // If same creation time (or both old), sort by start time descending
      const getStartTime = (booking: Booking) => {
        if (booking.tripStartDateTime) {
          return new Date(booking.tripStartDateTime).getTime();
        }
        // Fallback to bookingDate (use start of day as default)
        if (booking.bookingDate) {
          return new Date(booking.bookingDate).getTime();
        }
        return 0;
      };
      
      const timeA = getStartTime(a);
      const timeB = getStartTime(b);
      return timeB - timeA; // Latest timeslot first (descending)
    });
    
    return sortedUpcomingBookings;
  })();

  const pastBookings = bookings
    .filter(booking => booking.status === 'completed' || booking.status === 'cancelled')
    .sort((a, b) => {
      // Sort by trip end date/time (for driver bookings) or booking date descending
      const getEndDateTime = (booking: Booking) => {
        if (booking.numberOfNights && booking.numberOfNights > 0) {
          // Driver booking - calculate end date
          const startDate = new Date(booking.bookingDate);
          const endDate = new Date(startDate.getTime() + booking.numberOfNights * 24 * 60 * 60 * 1000);
          return endDate.getTime();
        }
        // Regular booking - use booking date
        return new Date(booking.bookingDate).getTime();
      };
      
      return getEndDateTime(b) - getEndDateTime(a); // Most recent end date first
    });

  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<{ id: string; status: string } | null>(null);

  const handleCancelBooking = async (bookingId: string) => {
    const booking = bookings.find(b => (b._id || b.id) === bookingId);
    if (!booking) return;

    const bookingDateTime = booking.tripStartDateTime 
      ? new Date(booking.tripStartDateTime)
      : new Date(booking.bookingDate);
    const now = new Date();
    const isPastBooking = bookingDateTime < now;

    let newStatus: 'pending' | 'confirmed' | 'completed' | 'cancelled' = 'cancelled';

    if (booking.status === 'confirmed' && isPastBooking) {
      // Past bookings should be marked as completed, not cancelled
      alert('This booking time has passed. It will be marked as completed.');
      return;
    }

    // Show cancellation reason modal
    setBookingToCancel({ id: bookingId, status: newStatus });
    setShowCancellationModal(true);
  };

  const handleConfirmCancellation = async (reason: string) => {
    if (!bookingToCancel) return;

    try {
      const response = await bookingsAPI.updateBookingStatus(bookingToCancel.id, bookingToCancel.status, reason);
      
      if (response.success) {
        // Refresh bookings list to get updated data
        await fetchBookings();
        setShowCancellationModal(false);
        setBookingToCancel(null);
        alert('Booking cancelled successfully');
      } else {
        alert(response.message || 'Failed to cancel booking');
      }
    } catch (error: any) {
      console.error('Cancellation error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to cancel booking';
      alert(errorMessage);
      // Still refresh bookings in case the cancellation partially succeeded
      fetchBookings();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-black">{t('bookings.title')}</h1>
          <p className="text-gray-600 mt-2">{t('bookings.subtitle')}</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {(!bookings || bookings.length === 0) ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-black mb-2">{t('bookings.noBookings')}</h3>
            <p className="text-gray-600 mb-6">
              {t('bookings.noBookingsSubtext')}
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-black text-white px-6 py-2 rounded-md hover:bg-gray-800"
            >
              {t('bookings.browseServices')}
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Upcoming Bookings Section */}
            {upcomingBookings.length > 0 && (
              <div>
                <div className="flex items-center mb-4">
                  <h2 className="text-xl font-bold text-black">{t('bookings.upcomingBookings')}</h2>
                  <span className="ml-3 px-3 py-1 bg-gray-100 text-gray-800 text-sm font-medium rounded-full">
                    {upcomingBookings.length}
                  </span>
                </div>
                <div className="space-y-6">
                  {upcomingBookings.map((booking) => {
                    const isDriver = booking.serviceType === 'driver' || booking.pickupLocation;
                    
                    return (
                      <div key={booking.id || booking._id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                        {/* Header with Gradient */}
                        <div className="bg-gradient-to-r from-black to-gray-600 px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                {isDriver ? <Navigation className="h-6 w-6 text-white" /> : <Calendar className="h-6 w-6 text-white" />}
                              </div>
                              <div>
                                <h3 className="text-lg font-bold text-white">
                                  {booking.serviceType || 'Service Booking'}
                                </h3>
                                <p className="text-white text-sm">
                                  ID: {(booking.bookingId || booking._id || booking.id)?.toString().slice(-8).toUpperCase()}
                                </p>
                              </div>
                            </div>
                            <div className={`px-4 py-2 rounded-lg border-2 flex items-center space-x-2 ${getStatusColor(booking.status)}`}>
                              {getStatusIcon(booking.status)}
                              <span className="font-semibold text-sm">
                                {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Body */}
                        <div className="p-6">
                          <div className="grid md:grid-cols-2 gap-6 mb-6">
                            {/* Provider Info */}
                            <div className="bg-white rounded-lg p-4">
                              <h4 className="font-semibold text-black mb-3 flex items-center">
                                <User className="h-4 w-4 mr-2 text-black" />
                                {t('bookings.providerDetails')}
                              </h4>
                              <div className="space-y-2 text-sm">
                                <p className="font-medium text-black">
                                  {(typeof booking.providerId === 'object' ? booking.providerId?.name : null) || booking.provider?.name || 'Provider'}
                                </p>
                                {booking.provider?.phone && (
                                  <a href={`tel:${booking.provider.phone}`} className="flex items-center text-black hover:text-gray-800">
                                    <Phone className="h-3 w-3 mr-2" />
                                    {booking.provider.phone}
                                  </a>
                                )}
                                {booking.provider?.email && (
                                  <a href={`mailto:${booking.provider.email}`} className="flex items-center text-black hover:text-gray-800">
                                    <Mail className="h-3 w-3 mr-2" />
                                    {booking.provider.email}
                                  </a>
                                )}
                              </div>
                            </div>

                            {/* Booking Info */}
                            <div className="bg-white rounded-lg p-4">
                              <h4 className="font-semibold text-black mb-3 flex items-center">
                                <Clock className="h-4 w-4 mr-2 text-black" />
                                {t('bookings.schedule')}
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">{t('bookings.bookedOn')}</span>
                                  <span className="font-medium text-black">
                                    {formatDateTime(booking.createdAt)}
                                  </span>
                                </div>
                                <div className="border-t border-gray-200 pt-2"></div>
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">{t('bookings.startDateTime')}</span>
                                  <span className="font-medium text-black">
                                    {getStartDateTime(booking)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">{t('bookings.endDateTime')}</span>
                                  <span className="font-medium text-black">
                                    {getEndDateTime(booking)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                  <span className="text-gray-600">{t('bookings.amount')}</span>
                                  <span className="font-bold text-green-600 flex items-center">
                                    <Banknote className="h-4 w-4 mr-1" />
                                    ₹{typeof booking.totalAmount === 'number' ? booking.totalAmount.toFixed(2) : booking.totalAmount || 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Driver Trip Route - if driver booking */}
                          {isDriver && booking.pickupLocation && booking.dropoffLocation && (
                            <div className="bg-blue-50 rounded-lg p-4 mb-6">
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                <MapPin className="h-4 w-4 mr-2 text-blue-600" />
                                {t('bookings.tripRoute')}
                              </h4>
                              <div className="space-y-3">
                                <div className="flex items-start">
                                  <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                                    <MapPin className="h-4 w-4 text-white" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-xs font-semibold text-green-700 mb-1">{t('bookings.pickup')}</p>
                                    <p className="text-sm text-gray-900">{booking.pickupLocation.address}</p>
                                  </div>
                                </div>
                                <div className="ml-4 border-l-2 border-gray-300 h-4"></div>
                                <div className="flex items-start">
                                  <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
                                    <MapPin className="h-4 w-4 text-white" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-xs font-semibold text-red-700 mb-1">{t('bookings.dropoff')}</p>
                                    <p className="text-sm text-gray-900">{booking.dropoffLocation.address}</p>
                                  </div>
                                </div>
                                {booking.totalDistance && (
                                  <div className="bg-white rounded-lg p-3 flex items-center justify-between mt-2">
                                    <span className="text-sm text-gray-600">{t('bookings.distance')}</span>
                                    <span className="text-sm font-bold text-gray-900">{typeof booking.totalDistance === 'number' ? booking.totalDistance.toFixed(2) : booking.totalDistance || '0.00'} km</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Notes */}
                          {booking.notes && (
                            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                              <p className="text-sm text-gray-700">
                                <span className="font-semibold">{t('bookings.notes')}</span> {booking.notes}
                              </p>
                            </div>
                          )}

                          {/* Action Buttons */}
                          {(booking.status === 'pending' || booking.status === 'confirmed') && (
                            <div className="flex flex-wrap gap-3 items-center justify-between pt-4 border-t border-gray-200">
                              <p className="text-sm text-gray-600">
                                {booking.status === 'pending' 
                                  ? `⏳ ${t('bookings.statusPending')}`
                                  : `✅ ${t('bookings.statusConfirmed')}`
                                }
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {/* View Trip button for confirmed driver bookings */}
                                {booking.status === 'confirmed' && booking.serviceType === 'driver' && (
                                  <button
                                    onClick={() => {
                                      const bookingId = booking._id || booking.id;
                                      if (bookingId) {
                                        navigate(`/trip/${bookingId}`);
                                      }
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center shadow-sm"
                                  >
                                    <MapPin className="h-4 w-4 mr-2" />
                                    {t('bookings.viewTrip')}
                                  </button>
                                )}
                                <button 
                                  onClick={() => {
                                    const bookingId = booking._id || booking.id;
                                    if (bookingId) {
                                      handleCancelBooking(bookingId);
                                    }
                                  }}
                                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium shadow-sm"
                                >
                                  {t('bookings.cancel')}
                                </button>
                                <button 
                                  onClick={() => {
                                    if (booking.provider?.phone) {
                                      window.location.href = `tel:${booking.provider.phone}`;
                                    } else {
                                      alert('Provider contact not available');
                                    }
                                  }}
                                  className="px-4 py-2 text-black bg-white rounded-lg hover:bg-gray-100 text-sm font-medium"
                                >
                                  {t('bookings.contact')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Past Bookings Section - Collapsible */}
            {pastBookings.length > 0 && (
              <div>
                <button
                  onClick={() => setShowPastBookings(!showPastBookings)}
                  className="w-full flex items-center justify-between p-4 bg-white rounded-lg hover:bg-white transition-colors"
                >
                  <div className="flex items-center">
                    <h2 className="text-xl font-bold text-black">{t('bookings.pastBookings')}</h2>
                    <span className="ml-3 px-3 py-1 bg-gray-200 text-gray-700 text-sm font-medium rounded-full">
                      {pastBookings.length}
                    </span>
                    {(() => {
                      const recentCount = pastBookings.filter(booking => {
                        const completedDate = new Date(booking.updatedAt);
                        const sevenDaysAgo = new Date();
                        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                        return completedDate >= sevenDaysAgo;
                      }).length;
                      
                      if (recentCount > 0) {
                        return (
                          <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full animate-pulse">
                            {recentCount} {t('bookings.recent')}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  {showPastBookings ? (
                    <ChevronUp className="h-5 w-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-600" />
                  )}
                </button>

                {showPastBookings && (
                  <div className="space-y-6 mt-4">
                    {pastBookings.map((booking) => {
                      const isDriver = booking.serviceType === 'driver' || booking.pickupLocation;
                      const isRecentCompletion = (() => {
                        const completedDate = new Date(booking.updatedAt);
                        const sevenDaysAgo = new Date();
                        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                        return completedDate >= sevenDaysAgo;
                      })();
                      
                      return (
                        <div key={booking.id || booking._id} className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden ${isRecentCompletion ? 'opacity-100 ring-2 ring-green-300' : 'opacity-75'}`}>
                          {/* Header with Gradient - Muted for past bookings */}
                          <div className={`px-6 py-4 ${isRecentCompletion ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-gray-500 to-gray-600'}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                  {isDriver ? <Navigation className="h-6 w-6 text-white" /> : <Calendar className="h-6 w-6 text-white" />}
                                </div>
                                <div>
                                  <h3 className="text-lg font-bold text-white">
                                    {booking.serviceType || 'Service Booking'}
                                  </h3>
                                  <p className="text-gray-200 text-sm">
                                    ID: {(booking.bookingId || booking._id || booking.id)?.toString().slice(-8).toUpperCase()}
                                  </p>
                                </div>
                              </div>
                              <div className={`px-4 py-2 rounded-lg border-2 flex items-center space-x-2 ${getStatusColor(booking.status)}`}>
                                {getStatusIcon(booking.status)}
                                <span className="font-semibold text-sm">
                                  {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Body */}
                          <div className="p-6">
                            <div className="grid md:grid-cols-2 gap-6 mb-4">
                              {/* Provider Info */}
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                  <User className="h-4 w-4 mr-2 text-gray-600" />
                                  {t('bookings.providerDetails')}
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <p className="font-medium text-black">
                                    {(typeof booking.providerId === 'object' ? booking.providerId?.name : null) || booking.provider?.name || 'Provider'}
                                  </p>
                                </div>
                              </div>

                              {/* Booking Info */}
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                  <Clock className="h-4 w-4 mr-2 text-gray-600" />
                                  {t('bookings.schedule')}
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600">{t('bookings.bookedOn')}</span>
                                    <span className="font-medium text-gray-900">
                                      {formatDateTime(booking.createdAt)}
                                    </span>
                                  </div>
                                  <div className="border-t border-gray-200 pt-2"></div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600">{t('bookings.startDateTime')}</span>
                                    <span className="font-medium text-gray-900">
                                      {getStartDateTime(booking)}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600">{t('bookings.endDateTime')}</span>
                                    <span className="font-medium text-gray-900">
                                      {getEndDateTime(booking)}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                    <span className="text-gray-600">{t('bookings.amount')}</span>
                                    <span className="font-bold text-green-600 flex items-center">
                                      <Banknote className="h-4 w-4 mr-1" />
                                      ₹{typeof booking.totalAmount === 'number' ? booking.totalAmount.toFixed(2) : booking.totalAmount || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Driver Trip Route - if driver booking */}
                            {isDriver && booking.pickupLocation && booking.dropoffLocation && (
                              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                  <MapPin className="h-4 w-4 mr-2 text-gray-600" />
                                  {t('bookings.tripRoute')}
                                </h4>
                                <div className="space-y-3">
                                  <div className="flex items-start">
                                    <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                                      <MapPin className="h-4 w-4 text-white" />
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-xs font-semibold text-green-700 mb-1">{t('bookings.pickup')}</p>
                                      <p className="text-sm text-gray-700">{booking.pickupLocation.address}</p>
                                    </div>
                                  </div>
                                  <div className="ml-4 border-l-2 border-gray-300 h-4"></div>
                                  <div className="flex items-start">
                                    <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
                                      <MapPin className="h-4 w-4 text-white" />
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-xs font-semibold text-red-700 mb-1">{t('bookings.dropoff')}</p>
                                      <p className="text-sm text-gray-700">{booking.dropoffLocation.address}</p>
                                    </div>
                                  </div>
                                  {booking.totalDistance && (
                                    <div className="bg-white rounded-lg p-3 flex items-center justify-between mt-2">
                                      <span className="text-sm text-gray-600">{t('bookings.distance')}</span>
                                      <span className="text-sm font-bold text-gray-900">{typeof booking.totalDistance === 'number' ? booking.totalDistance.toFixed(2) : booking.totalDistance || '0.00'} km</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Status Message */}
                            {booking.status === 'completed' && (
                              <div>
                                <div className="bg-green-50 rounded-lg p-4 flex items-center justify-center mb-4">
                                  <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                                  <span className="text-sm font-medium text-green-700">{t('bookings.serviceCompleted')}</span>
                                </div>
                                
                                {/* Rating section for driver bookings */}
                                {isDriver && (
                                  <div className="border-t border-gray-200 pt-4">
                                    {booking.driverRating?.rating ? (
                                      <div className="bg-yellow-50 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-sm font-semibold text-gray-900">{t('bookings.yourRating')}</span>
                                          <div className="flex items-center">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                              <Star
                                                key={star}
                                                className={`h-4 w-4 ${
                                                  star <= (booking.driverRating?.rating || 0)
                                                    ? 'fill-yellow-400 text-yellow-400'
                                                    : 'text-gray-300'
                                                }`}
                                              />
                                            ))}
                                            <span className="ml-2 text-sm font-bold text-gray-700">
                                              {booking.driverRating.rating}/5
                                            </span>
                                          </div>
                                        </div>
                                        {booking.driverRating.comment && (
                                          <p className="text-sm text-gray-700 italic">"{booking.driverRating.comment}"</p>
                                        )}
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => {
                                          setSelectedBookingForRating(booking);
                                          setRatingModalOpen(true);
                                        }}
                                        className="w-full px-4 py-3 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 transition-colors font-medium flex items-center justify-center shadow-sm"
                                      >
                                        <Star className="h-5 w-5 mr-2" />
                                        {t('bookings.rateYourDriver')}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {booking.status === 'cancelled' && (
                              <div className="bg-red-50 rounded-lg p-4 flex items-center justify-center">
                                <XCircle className="h-5 w-5 text-red-600 mr-2" />
                                <span className="text-sm font-medium text-red-700">{t('bookings.bookingCancelled')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rating Modal */}
      {selectedBookingForRating && (
        <RatingModal
          isOpen={ratingModalOpen}
          onClose={() => {
            setRatingModalOpen(false);
            setSelectedBookingForRating(null);
          }}
          bookingId={selectedBookingForRating._id || selectedBookingForRating.id || ''}
          driverName={
            (typeof selectedBookingForRating.providerId === 'object' 
              ? selectedBookingForRating.providerId?.name 
              : null) || 
            selectedBookingForRating.provider?.name || 
            'Your Driver'
          }
          onRatingSubmitted={() => {
            // Refresh bookings to show the new rating
            fetchBookings();
          }}
        />
      )}
      
      <CancellationReasonModal
        isOpen={showCancellationModal}
        onClose={() => {
          setShowCancellationModal(false);
          setBookingToCancel(null);
        }}
        onConfirm={handleConfirmCancellation}
      />
    </div>
  );
};

export default MyBookingsPage; 